import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiEye, FiSearch } from 'react-icons/fi';
import { orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
} from '../../components/UI';
import { useFirestoreCollection, usePagination, useUserDirectory } from '../../hooks';
import { updateOrderStatus } from '../../services/adminService';
import styles from './Orders.module.css';

// Schema (verified against production documents, 2026-08-21):
//   orders.{ orderId, customerId, businessIds[], businessOwnerIds[], items[],
//            itemCount, subtotal, shipping, tax, total, currency, status,
//            statusHistory[], shippingAddress{ name, line1, line2, city, state,
//            zip, phone }, paymentMethod, transactionId, trackingNumber,
//            carrier, createdAt, updatedAt }
// Note there is NO customerName / customerEmail / totalAmount / postalCode on
// these documents — the app never writes them. The previous code read exactly
// those fields, which is why Customer rendered a raw uid and Total rendered "-".
// Names now come from the live users directory; money comes from `total`.
const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

const adminActions = [
  { value: 'paid', label: 'Mark Paid' },
  { value: 'processing', label: 'Mark Processing' },
  { value: 'shipped', label: 'Mark Shipped' },
  { value: 'delivered', label: 'Mark Delivered' },
  { value: 'cancelled', label: 'Cancel' },
  { value: 'refunded', label: 'Mark Refunded' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

// Production orders store the charged figure in `total`; `totalAmount` is only
// kept as a fallback for any legacy document.
const orderTotal = (o) => (o?.total != null ? o.total : o?.totalAmount);

// The doc id IS the order number (ORD-<epoch>-<suffix>). Slicing it to 8 chars
// collapsed every 2026 order to the same "ORD-1779" string in the table.
const orderNumber = (o) => o?.orderId || o?.id || '-';

// paymentStatus is what the buyer's app wrote; only the Stripe webhook sets
// paymentConfirmedServerSide (and flags a payment it refused). A "completed"
// claim the server has not confirmed is shown as such, never as paid.
const REVIEW_FLAGS = [
  ['amountMismatch', 'Amount mismatch'],
  ['payerMismatch', 'Not written by the payer'],
  ['destinationRefused', 'Paid to another seller'],
  ['fulfilmentAmbiguous', 'Several orders for one payment'],
  ['fulfilmentKindMismatch', 'Payment for another kind of record'],
];
const paymentLabel = (o) => {
  const ps = o?.paymentStatus || '';
  const flags = REVIEW_FLAGS.filter(([f]) => o?.[f] === true).map(([, label]) => label);
  if (ps === 'under_review' || flags.length) return `under review${flags.length ? ` (${flags.join(', ')})` : ''}`;
  if (o?.paymentConfirmedServerSide === true) return `${ps || 'completed'} (confirmed)`;
  if (ps === 'completed') return 'completed (not confirmed by Stripe)';
  return ps || '-';
};

const formatMoney = (amount, currency = 'EUR') => {
  if (amount == null) return '-';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount));
  } catch {
    return `${amount} ${currency}`;
  }
};

const Orders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const constraints = useMemo(
    () => [orderBy('createdAt', 'desc'), limit(500)],
    []
  );
  const { data: orders, loading } = useFirestoreCollection('orders', constraints);
  const directory = useUserDirectory();

  useEffect(() => {
    if (statusFilter !== 'all') setSearchParams({ status: statusFilter });
    else setSearchParams({});
  }, [statusFilter, setSearchParams]);

  const filtered = useMemo(() => {
    let list = orders;
    if (statusFilter !== 'all') list = list.filter((o) => o.status === statusFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (o) =>
          (o.id || '').toLowerCase().includes(q) ||
          (o.orderId || '').toLowerCase().includes(q) ||
          (o.trackingNumber || '').toLowerCase().includes(q) ||
          (o.discountCode || '').toLowerCase().includes(q) ||
          // Resolved name / handle / email of the customer, not just the uid.
          directory.searchBlobOf(o.customerId).includes(q) ||
          (o.shippingAddress?.name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, statusFilter, searchQuery, directory]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    if (!window.confirm(`Change order ${selected.id} status to "${newStatus}"?`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await updateOrderStatus(selected.id, newStatus);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Update failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status) => {
    const variants = {
      pending: 'warning',
      paid: 'primary',
      processing: 'secondary',
      shipped: 'tertiary',
      delivered: 'success',
      cancelled: 'inactive',
      refunded: 'danger',
    };
    return <Badge variant={variants[status] || 'inactive'}>{status || 'unknown'}</Badge>;
  };

  const csvColumns = [
    { key: 'id', label: 'Order ID', accessor: (o) => orderNumber(o) },
    { key: 'customerId', label: 'Customer UID' },
    { key: 'customerName', label: 'Customer Name', accessor: (o) => directory.nameOf(o.customerId, o.shippingAddress?.name || '') },
    { key: 'customerEmail', label: 'Customer Email', accessor: (o) => directory.emailOf(o.customerId) },
    { key: 'status', label: 'Status' },
    { key: 'subtotal', label: 'Subtotal' },
    { key: 'shipping', label: 'Shipping' },
    { key: 'tax', label: 'Tax' },
    { key: 'discountCode', label: 'Discount Code' },
    { key: 'discountValue', label: 'Discount', accessor: (o) => ((o.discountCents || 0) / 100) || '' },
    { key: 'total', label: 'Total', accessor: (o) => orderTotal(o) },
    { key: 'currency', label: 'Currency' },
    {
      key: 'itemCount',
      label: 'Items',
      accessor: (o) => (Array.isArray(o.items) ? o.items.length : o.itemCount ?? 0),
    },
    { key: 'transactionId', label: 'Stripe PaymentIntent' },
    { key: 'payment', label: 'Payment', accessor: (o) => paymentLabel(o) },
    { key: 'createdAt', label: 'Created', accessor: (o) => formatDate(o.createdAt) },
  ];

  const columns = [
    {
      key: 'id',
      title: 'Order',
      render: (_, row) => <code style={{ fontSize: 12 }}>{orderNumber(row)}</code>,
    },
    {
      key: 'customer',
      title: 'Customer',
      render: (_, row) => {
        // shippingAddress.name is the recipient the buyer typed — a decent
        // fallback for a uid that no longer resolves (deleted account).
        const name = directory.nameOf(row.customerId, row.shippingAddress?.name || '');
        const email = directory.emailOf(row.customerId);
        const handle = directory.handleOf(row.customerId);
        return (
          <div className={styles.customerCell}>
            <span className={styles.customerName}>{name || '-'}</span>
            <span className={styles.customerMeta}>{email || handle || row.customerId || ''}</span>
          </div>
        );
      },
    },
    {
      key: 'total',
      title: 'Total',
      render: (_, row) => (
        <div>
          <div>{formatMoney(orderTotal(row), row.currency)}</div>
          {row.discountCode && (
            <div className={styles.customerMeta}>{row.discountCode}</div>
          )}
        </div>
      ),
    },
    {
      key: 'items',
      title: 'Items',
      render: (value, row) => (Array.isArray(value) ? value.length : row.itemCount ?? 0),
    },
    { key: 'status', title: 'Status', render: (value) => statusBadge(value) },
    { key: 'payment', title: 'Payment', render: (_, row) => paymentLabel(row) },
    { key: 'createdAt', title: 'Placed', render: (v) => formatDate(v) },
    {
      key: 'actions',
      title: '',
      width: '80px',
      render: (_, row) => (
        <Button
          variant="ghost"
          size="sm"
          icon={<FiEye />}
          onClick={() => {
            setSelected(row);
            setShowDetail(true);
          }}
        />
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Orders</h1>
          {!loading && <Badge variant="primary">{orders.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          Manage customer orders across all businesses
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search by id / customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
          <div className={styles.filters}>
            {statusOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterBtn} ${statusFilter === option.value ? styles.active : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CsvExportButton rows={filtered} columns={csvColumns} filename={`dierend-orders-${statusFilter}`} />
        </div>

        <Table columns={columns} data={pageData} loading={loading} emptyMessage="No orders found" />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        title={selected ? `Order ${orderNumber(selected)}` : 'Order Details'}
        size="lg"
        footer={
          selected && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {adminActions.map((a) => (
                <Button
                  key={a.value}
                  variant={a.value === 'cancelled' || a.value === 'refunded' ? 'danger' : 'primary'}
                  size="sm"
                  disabled={actionLoading || selected.status === a.value}
                  onClick={() => handleStatusChange(a.value)}
                >
                  {a.label}
                </Button>
              ))}
            </div>
          )
        }
      >
        {selected && (
          <div className={styles.detailContent}>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Order ID</span>
                <span className={styles.detailValue}>{orderNumber(selected)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Status</span>
                <span className={styles.detailValue}>{statusBadge(selected.status)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Payment</span>
                <span className={styles.detailValue}>{paymentLabel(selected)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Customer</span>
                <span className={styles.detailValue}>
                  {directory.nameOf(selected.customerId, selected.shippingAddress?.name || '') || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Customer email</span>
                <span className={styles.detailValue}>
                  {directory.emailOf(selected.customerId) || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Username</span>
                <span className={styles.detailValue}>
                  {directory.handleOf(selected.customerId) || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Customer UID</span>
                <span className={styles.detailValue}>{selected.customerId || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Subtotal</span>
                <span className={styles.detailValue}>
                  {formatMoney(selected.subtotal, selected.currency)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Shipping</span>
                <span className={styles.detailValue}>
                  {formatMoney(selected.shipping, selected.currency)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Tax</span>
                <span className={styles.detailValue}>
                  {formatMoney(selected.tax, selected.currency)}
                </span>
              </div>
              {selected.discountCode && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Discount</span>
                  <span className={styles.detailValue}>
                    {selected.discountCode} · −
                    {formatMoney((selected.discountCents || 0) / 100, selected.currency)}
                  </span>
                </div>
              )}
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Total</span>
                <span className={styles.detailValue}>
                  {formatMoney(orderTotal(selected), selected.currency)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Payment</span>
                <span className={styles.detailValue}>{selected.transactionId || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Created</span>
                <span className={styles.detailValue}>{formatDate(selected.createdAt)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Updated</span>
                <span className={styles.detailValue}>{formatDate(selected.updatedAt)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Tracking</span>
                <span className={styles.detailValue}>
                  {selected.trackingNumber ? `${selected.trackingNumber} (${selected.carrier || '-'})` : '-'}
                </span>
              </div>
            </div>

            {Array.isArray(selected.items) && selected.items.length > 0 && (
              <div className={styles.section}>
                <h4>Items ({selected.items.length})</h4>
                <ul>
                  {selected.items.map((item, i) => (
                    <li key={i} className={styles.itemRow}>
                      {item.image && (
                        <img src={item.image} alt="" className={styles.itemThumb} />
                      )}
                      <span>
                        {item.name || item.productName || `Item ${i + 1}`}
                        {item.quantity ? ` × ${item.quantity}` : ''}
                        {item.price != null
                          ? ` — ${formatMoney(item.price, item.currency || selected.currency)}`
                          : ''}
                        {item.returnStatus ? ` · return: ${item.returnStatus}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selected.shippingAddress && (
              <div className={styles.section}>
                <h4>Shipping</h4>
                <p style={{ whiteSpace: 'pre-line' }}>
                  {typeof selected.shippingAddress === 'string'
                    ? selected.shippingAddress
                    : [
                        selected.shippingAddress.name,
                        selected.shippingAddress.line1,
                        selected.shippingAddress.line2,
                        // The app writes `zip`; `postalCode` is only a legacy fallback.
                        [selected.shippingAddress.zip || selected.shippingAddress.postalCode,
                          selected.shippingAddress.city].filter(Boolean).join(' '),
                        selected.shippingAddress.state,
                        selected.shippingAddress.country,
                        selected.shippingAddress.phone,
                      ]
                        .filter(Boolean)
                        .join('\n')}
                </p>
              </div>
            )}

            {Array.isArray(selected.statusHistory) && selected.statusHistory.length > 0 && (
              <div className={styles.section}>
                <h4>Status History</h4>
                <ul>
                  {selected.statusHistory.map((h, i) => (
                    <li key={i}>
                      <strong>{h.status}</strong> — {formatDate(h.at)} ({h.actor || 'system'})
                      {h.notes ? ` · ${h.notes}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Orders;
